/**
 * useGoogleClientSummaries - React Query hook for the /clients Google and
 * Growth columns
 *
 * Fetches every client of the caller's agency for one month in a single
 * request, plus the agency's growth over them. A null source is not linked
 * to that client.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";
import type { GrowthPillarKey, GrowthSource } from "./useGrowth";

/** A source's line on a month that has not settled: day 1 to `through`
 *  against day 1 to `previousThrough` of the month before. */
export interface GoogleSummaryWindow {
  through: string;
  previousThrough: string;
}

export interface GoogleClientSummary {
  growth: {
    rate: number | null;
    complete: boolean;
    /** Not complete, so the rate compares same-days windows. */
    provisional: boolean;
    finalFrom: string | null;
    overdue: GrowthSource[];
    pillars: Array<{ key: GrowthPillarKey; label: string; changePct: number | null }>;
  };
  /** `complete` false = the month has not settled for that source, so its
   *  change would read as a fall; `toDate` then holds its same-days pair. */
  businessProfile: {
    actions: number | null;
    actionsPrevious: number | null;
    complete: boolean;
    toDate: (GoogleSummaryWindow & { actions: number | null; actionsPrevious: number | null }) | null;
  } | null;
  website: {
    sessions: number | null;
    sessionsPrevious: number | null;
    complete: boolean;
    toDate: (GoogleSummaryWindow & { sessions: number | null; sessionsPrevious: number | null }) | null;
  } | null;
  googleAds: {
    complete: boolean;
    clicks: number | null;
    clicksPrevious: number | null;
    costMinor: number | null;
    conversions: number | null;
    toDate: (GoogleSummaryWindow & { clicks: number | null; clicksPrevious: number | null }) | null;
  } | null;
}

export type GoogleClientSummaryMap = Record<string, GoogleClientSummary>;

/** Medians over the agency's ACTIVE clients, not a pooled total, so one
 *  large client or outlier cannot carry the number. Flat = rate within
 *  [-2, +2]. Only final rates count. */
export interface AgencyGrowth {
  rate: number | null;
  clientsWithRate: number;
  growing: number;
  flat: number;
  declining: number;
  /** Clients whose rate is still provisional, left out of the median. */
  provisional: number;
  pillars: Array<{ key: GrowthPillarKey; label: string; changePct: number | null }>;
}

export interface GoogleClientSummaries {
  clients: GoogleClientSummaryMap;
  agency: AgencyGrowth;
}

const googleClientSummaryKeys = {
  all: (year: number, month: number) =>
    ["google-client-summaries", year, month] as const,
};

export function useGoogleClientSummaries(year: number, month: number) {
  return useQuery({
    queryKey: googleClientSummaryKeys.all(year, month),
    queryFn: async (): Promise<GoogleClientSummaries> => {
      const response = await apiClient.get<GoogleClientSummaries>(
        "/api/google/client-summaries",
        { params: { year, month } }
      );
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });
}
